/**
 * P0 Feedback System - For bug reports, feature requests, general feedback
 * This is separate from patient feedback on doctors
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
  Feedback as FeedbackIcon,
  Support as SupportIcon,
  Send as SendIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import feedbackApi from '../../api/feedbackApi';

const P0Feedback = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    feedback_type: 'general',
    subject: '',
    message: '',
    priority: 'medium',
    page_url: window.location.href,
  });

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const response = await feedbackApi.list();
      setFeedbackList(response.results || response || []);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback history');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await feedbackApi.create(formData);
      toast.success('Feedback submitted successfully!');
      setDialogOpen(false);
      setFormData({
        feedback_type: 'general',
        subject: '',
        message: '',
        priority: 'medium',
        page_url: window.location.href,
      });
      fetchFeedback();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const getFeedbackTypeIcon = (type) => {
    const icons = {
      bug: <BugIcon />,
      feature_request: <FeatureIcon />,
      general: <FeedbackIcon />,
      support: <SupportIcon />,
    };
    return icons[type] || <FeedbackIcon />;
  };

  const getFeedbackTypeLabel = (type) => {
    const labels = {
      bug: 'Bug Report',
      feature_request: 'Feature Request',
      general: 'General Feedback',
      support: 'Support Request',
    };
    return labels[type] || type;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      medium: 'primary',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'default',
      acknowledged: 'info',
      in_progress: 'warning',
      resolved: 'success',
      closed: 'default',
    };
    return colors[status] || 'default';
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Feedback</Typography>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Submit Feedback
        </Button>
      </Box>

      {/* Submit Feedback Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Feedback Type</InputLabel>
                <Select
                  value={formData.feedback_type}
                  onChange={(e) => setFormData({ ...formData, feedback_type: e.target.value })}
                  label="Feedback Type"
                >
                  <MenuItem value="bug">Bug Report</MenuItem>
                  <MenuItem value="feature_request">Feature Request</MenuItem>
                  <MenuItem value="general">General Feedback</MenuItem>
                  <MenuItem value="support">Support Request</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback History */}
      <Paper>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Feedback History
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : feedbackList.length === 0 ? (
            <Alert severity="info">No feedback submitted yet</Alert>
          ) : (
            <List>
              {feedbackList.map((feedback) => (
                <ListItem
                  key={feedback.id}
                  sx={{
                    borderLeft: `3px solid`,
                    borderColor: `${getPriorityColor(feedback.priority)}.main`,
                    mb: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getFeedbackTypeIcon(feedback.feedback_type)}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {feedback.subject}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Chip
                          label={getFeedbackTypeLabel(feedback.feedback_type)}
                          size="small"
                          icon={getFeedbackTypeIcon(feedback.feedback_type)}
                        />
                        <Chip
                          label={feedback.priority}
                          size="small"
                          color={getPriorityColor(feedback.priority)}
                        />
                        <Chip
                          label={feedback.status}
                          size="small"
                          color={getStatusColor(feedback.status)}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {feedback.message}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {feedback.created_at
                          ? format(new Date(feedback.created_at), 'PPp')
                          : 'Date not available'}
                      </Typography>
                      {feedback.response && (
                        <Chip
                          label="Response Available"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {feedback.response && (
                      <Box mt={2} p={2} bgcolor="background.default" borderRadius={1}>
                        <Typography variant="caption" color="text.secondary">
                          Response:
                        </Typography>
                        <Typography variant="body2">{feedback.response}</Typography>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default P0Feedback;

